import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, Phone, Video, Loader2, ClipboardList, MessageCircle, MessageSquare, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDndStatus } from '@/hooks/useDndStatus';
import { useCallNotificationsState } from '@/hooks/useCallNotificationsState';
import { useTaskNotifications } from '@/hooks/useTaskNotifications';
import { useWhatsAppNotifications } from '@/hooks/useWhatsAppNotifications';
import { useFeedbackNotifications } from '@/hooks/useFeedbackNotifications';
import { useEscalationNotifications } from '@/hooks/useEscalationNotifications';
import { useVideoMeetingNotifications } from '@/hooks/useVideoMeetingNotifications';
import { formatDistanceToNow } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';

const DATE_LOCALES = { 'pt-BR': ptBR, 'en-US': enUS, 'es': es } as const;

const PRIORITY_STYLES: Record<string, string> = {
  urgent: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-muted text-muted-foreground',
};

const PRIORITY_LABELS: Record<string, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

export function NotificationsPanel() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isDnd, isLoading: isDndLoading, toggleDnd, isToggling } = useDndStatus();
  const { notifications, unreadCount, markAsRead } = useCallNotificationsState();
  const { taskNotifications, unreadTaskCount, markTaskAsRead } = useTaskNotifications();
  const { unreadCount: whatsappUnread, markAllAsRead, isMarkingAsRead } = useWhatsAppNotifications();
  const { openCount: feedbackOpen, isAdmin, markAllAsReviewed, isMarkingAsReviewed } = useFeedbackNotifications();
  const { pendingForMe, respondedToMe, unreadCount: escalationUnread, markAsRead: markEscalationAsRead } = useEscalationNotifications();
  const { meetings: videoMeetings, unreadCount: videoMeetingUnread, markAsRead: markVideoMeetingAsRead } = useVideoMeetingNotifications();

  const dateLocale = DATE_LOCALES[i18n.language] || ptBR;
  const totalUnread = unreadCount + unreadTaskCount + whatsappUnread + escalationUnread + videoMeetingUnread + (isAdmin ? feedbackOpen : 0);
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (isDndLoading) {
    return (
      <Button variant="ghost" size="icon" disabled>
        <Loader2 className="h-5 w-5 animate-spin" />
      </Button>
    );
  }

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          {isDnd ? (
            <BellOff className="h-5 w-5 text-destructive" />
          ) : (
            <Bell className="h-5 w-5" />
          )}
          {!isDnd && totalUnread > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
              variant="destructive"
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" modal={false}>
        {/* DND Toggle Section */}
        <div className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BellOff className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t('topbar.doNotDisturb')}</span>
          </div>
          <Switch
            checked={isDnd}
            onCheckedChange={toggleDnd}
            disabled={isToggling}
          />
        </div>
        
        {isDnd && (
          <p className="px-3 pb-2 text-xs text-muted-foreground">
            {t('topbar.dndActiveMessage')}
          </p>
        )}

        <Separator />

        {/* WhatsApp Messages Section */}
        <div className="flex items-center">
          <button
            className="flex-1 flex items-center gap-3 p-3 hover:bg-accent text-left transition-colors"
            onClick={() => {
              setPopoverOpen(false);
              setTimeout(() => navigate('/whatsapp?filter=minhas'), 150);
            }}
          >
            <MessageCircle className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">{t('topbar.whatsappMessages', 'Mensagens WhatsApp')}</p>
              <p className="text-xs text-muted-foreground">
                {whatsappUnread > 0
                  ? t('topbar.unreadMessages', '{{count}} mensagens não lidas', { count: whatsappUnread })
                  : t('topbar.noUnreadMessages', 'Nenhuma mensagem não lida')}
              </p>
            </div>
            {whatsappUnread > 0 && (
              <Badge variant="destructive" className="text-xs px-1.5 py-0.5">
                {whatsappUnread > 9 ? '9+' : whatsappUnread}
              </Badge>
            )}
          </button>
          {whatsappUnread > 0 && (
            <button
              className="p-2 mr-1 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
              title={t('topbar.markAllAsRead', 'Marcar tudo como lido')}
              disabled={isMarkingAsRead}
              onClick={(e) => {
                e.stopPropagation();
                markAllAsRead();
              }}
            >
              {isMarkingAsRead ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
            </button>
          )}
        </div>

        <Separator />

        {/* Video Meetings Section */}
        <div className="p-3 pb-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Video className="h-4 w-4" />
            Reuniões de Vídeo
            {videoMeetingUnread > 0 && (
              <Badge variant="destructive" className="text-xs px-1.5 py-0.5 ml-auto">
                {videoMeetingUnread > 9 ? '9+' : videoMeetingUnread}
              </Badge>
            )}
          </div>
        </div>

        {videoMeetings.length === 0 ? (
          <div className="p-4 pt-0 text-sm text-muted-foreground text-center">
            Nenhuma reunião de vídeo agendada
          </div>
        ) : (
          <div className="max-h-[200px] overflow-y-auto pb-2">
            {videoMeetings.map((meeting) => (
              <button
                key={meeting.id}
                className="w-full flex items-start gap-3 p-3 hover:bg-accent text-left transition-colors"
                onClick={() => {
                  markVideoMeetingAsRead(meeting.id);
                  setPopoverOpen(false);
                  setTimeout(() => navigate('/agenda'), 150);
                }}
              >
                <Video className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {meeting.title || 'Reunião de Vídeo'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(meeting.scheduled_for), {
                      addSuffix: true,
                      locale: dateLocale,
                    })}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        <Separator />
        <div className="p-3 pb-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Phone className="h-4 w-4" />
            {t('topbar.upcomingCalls')}
          </div>
        </div>

        {notifications.length === 0 ? (
          <div className="p-4 pt-0 text-sm text-muted-foreground text-center">
            {t('topbar.noUpcomingCalls')}
          </div>
        ) : (
          <div className="max-h-[200px] overflow-y-auto pb-2">
            {notifications.map((notification) => (
              <button
                key={notification.id}
                className="w-full flex items-start gap-3 p-3 hover:bg-accent text-left transition-colors"
                onClick={() => {
                  markAsRead(notification.id);
                  setPopoverOpen(false);
                  setTimeout(() => navigate('/calls'), 150);
                }}
              >
                <Phone className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {notification.title || t('topbar.scheduledCall')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(notification.scheduled_for), { 
                      addSuffix: true, 
                      locale: dateLocale 
                    })}
                  </p>
                  {notification.call_purpose && (
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {notification.call_purpose}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        <Separator />

        {/* Task Notifications Section */}
        <div className="p-3 pb-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ClipboardList className="h-4 w-4" />
            {t('topbar.assignedTasks', 'Tarefas Atribuídas')}
          </div>
        </div>

        {taskNotifications.length === 0 ? (
          <div className="p-4 pt-0 text-sm text-muted-foreground text-center">
            {t('topbar.noAssignedTasks', 'Nenhuma tarefa recente')}
          </div>
        ) : (
          <div className="max-h-[200px] overflow-y-auto pb-2">
            {taskNotifications.map((task) => (
              <button
                key={task.id}
                className="w-full flex items-start gap-3 p-3 hover:bg-accent text-left transition-colors"
                onClick={() => {
                  markTaskAsRead(task.id);
                  setPopoverOpen(false);
                  setTimeout(() => navigate(`/agenda?view=list&taskId=${task.id}`), 150);
                }}
              >
                <ClipboardList className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate flex-1">
                      {task.title}
                    </p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.low}`}>
                      {PRIORITY_LABELS[task.priority] || task.priority}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(task.created_at), {
                      addSuffix: true,
                      locale: dateLocale,
                    })}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Escalation Notifications Section */}
        {(pendingForMe.length > 0 || respondedToMe.length > 0) && (
          <>
            <Separator />
            <div className="p-3 pb-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <AlertTriangle className="h-4 w-4" />
                {t('topbar.escalations', 'Escalações')}
                {escalationUnread > 0 && (
                  <Badge variant="destructive" className="text-xs px-1.5 py-0.5 ml-auto">
                    {escalationUnread > 9 ? '9+' : escalationUnread}
                  </Badge>
                )}
              </div>
            </div>

            {/* Pending for me */}
            {pendingForMe.length > 0 && (
              <div className="max-h-[150px] overflow-y-auto">
                {pendingForMe.map((esc) => (
                  <button
                    key={esc.id}
                    className="w-full flex items-start gap-3 p-3 hover:bg-accent text-left transition-colors bg-amber-50/50 dark:bg-amber-950/20"
                    onClick={() => {
                      markEscalationAsRead(esc.id);
                      setPopoverOpen(false);
                      const target = esc.task_id ? `/agenda?view=list&taskId=${esc.task_id}&fromNotification=1` : `/customer-service?session=${esc.session_id}`;
                      setTimeout(() => navigate(target), 150);
                    }}
                  >
                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                        {t('topbar.pendingEscalation', 'Pendente para você')}
                      </p>
                      <p className="text-sm truncate">{esc.question}</p>
                      <p className="text-xs text-muted-foreground">
                        {esc.from_user_name && `${t('topbar.from', 'De')}: ${esc.from_user_name} · `}
                        {formatDistanceToNow(new Date(esc.escalated_at), { addSuffix: true, locale: dateLocale })}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Responded to me */}
            {respondedToMe.length > 0 && (
              <div className="max-h-[150px] overflow-y-auto">
                {respondedToMe.map((esc) => (
                  <button
                    key={esc.id}
                    className="w-full flex items-start gap-3 p-3 hover:bg-accent text-left transition-colors bg-green-50/50 dark:bg-green-950/20"
                    onClick={() => {
                      markEscalationAsRead(esc.id);
                      setPopoverOpen(false);
                      const target = esc.task_id ? `/agenda?view=list&taskId=${esc.task_id}&fromNotification=1` : `/customer-service?session=${esc.session_id}`;
                      setTimeout(() => navigate(target), 150);
                    }}
                  >
                    <Check className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-green-700 dark:text-green-400">
                        {t('topbar.respondedEscalation', 'Resposta recebida')}
                      </p>
                      <p className="text-sm truncate">{esc.response || esc.question}</p>
                      <p className="text-xs text-muted-foreground">
                        {esc.to_user_name && `${t('topbar.respondedBy', 'Por')}: ${esc.to_user_name} · `}
                        {esc.responded_at && formatDistanceToNow(new Date(esc.responded_at), { addSuffix: true, locale: dateLocale })}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Feedback Section - Admin only */}
        {isAdmin && (
          <>
            <Separator />
            <div className="flex items-center">
              <button
                className="flex-1 flex items-center gap-3 p-3 hover:bg-accent text-left transition-colors"
                onClick={() => {
                  setPopoverOpen(false);
                  setTimeout(() => navigate('/product-feedback'), 150);
                }}
              >
                <MessageSquare className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{t('topbar.feedback', 'Feedback')}</p>
                  <p className="text-xs text-muted-foreground">
                    {feedbackOpen > 0
                      ? t('topbar.openReports', '{{count}} reports abertos', { count: feedbackOpen })
                      : t('topbar.noOpenReports', 'Nenhum report aberto')}
                  </p>
                </div>
                {feedbackOpen > 0 && (
                  <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-orange-100 text-orange-800">
                    {feedbackOpen > 9 ? '9+' : feedbackOpen}
                  </Badge>
                )}
              </button>
              {feedbackOpen > 0 && (
                <button
                  className="p-2 mr-1 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                  title={t('topbar.markAllAsRead', 'Marcar tudo como lido')}
                  disabled={isMarkingAsReviewed}
                  onClick={(e) => {
                    e.stopPropagation();
                    markAllAsReviewed();
                  }}
                >
                  {isMarkingAsReviewed ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
